/*
 * Copyright (c) 2021 WIT Software. All rights reserved.
 *
 * WIT Software Confidential and Proprietary information. It is strictly forbidden for 3rd parties to modify, decompile,
 * disassemble, defeat, disable or circumvent any protection mechanism; to sell, license, lease, rent, redistribute or
 * make accessible to any third party, whether for profit or without charge.
 *
 */

import { put, takeLeading, select, race, take, fork } from 'redux-saga/effects';
import { SagaIterator } from 'redux-saga';
import {
  UpdatablePreference,
  GdprUserPreference,
  ActionRequest,
  ActionSuccess,
  ActionFail,
  XError,
  AsyncActionStateNoData,
  GdprErrors,
  User,
  Role,
  GdprDocuments,
  Ducks,
  GdprDocument,
  ActionsEventActionNames,
} from '@wit/vtv-x-foundation-lib';
import { StoreState } from 'store/store';
import { addSettingsActionEvent } from '../../../store/ducks/analytics/actionsEvent';

export enum ActionTypes {
  UPDATE_PREFERENCE = 'gdpr/preference/UPDATE',
  UPDATE_PREFERENCE_SUCCESS = 'gdpr/preference/UPDATE_SUCCESS',
  UPDATE_PREFERENCE_FAIL = 'gdpr/preference/UPDATE_FAIL',
}

interface UpdatePreferenceMeta {
  preference: UpdatablePreference;
  preferenceValue: GdprUserPreference;
}

type UpdatePreference = ActionRequest<ActionTypes.UPDATE_PREFERENCE, UpdatePreferenceMeta>;
type UpdatePreferenceSuccess = ActionSuccess<ActionTypes.UPDATE_PREFERENCE_SUCCESS>;
type UpdatePreferenceFailed = ActionFail<ActionTypes.UPDATE_PREFERENCE_FAIL>;
type Actions = UpdatePreference | UpdatePreferenceSuccess | UpdatePreferenceFailed;

const Actions = {
  updatePreference: (preference: UpdatablePreference, preferenceValue: GdprUserPreference): UpdatePreference => ({
    type: ActionTypes.UPDATE_PREFERENCE,
    meta: { preference, preferenceValue },
  }),
  updatePreferenceSuccess: (): UpdatePreferenceSuccess => ({ type: ActionTypes.UPDATE_PREFERENCE_SUCCESS }),
  updatePreferenceFail: (error: XError): UpdatePreferenceFailed => ({
    type: ActionTypes.UPDATE_PREFERENCE_FAIL,
    error,
  }),
};

export const ActionCreators = {
  updatePreference: Actions.updatePreference,
};

// REDUCERS
const initialState: AsyncActionStateNoData = {
  processing: false,
  processed: false,
  error: undefined,
};

export default function gdpr(state = initialState, action: Actions): AsyncActionStateNoData {
  switch (action.type) {
    case ActionTypes.UPDATE_PREFERENCE:
      return {
        ...state,
        processing: true,
        processed: false,
      };
    case ActionTypes.UPDATE_PREFERENCE_SUCCESS:
      return {
        ...state,
        processing: false,
        processed: true,
      };
    case ActionTypes.UPDATE_PREFERENCE_FAIL:
      return {
        ...state,
        processing: false,
        processed: true,
        error: action.error,
      };
    default:
      return state;
  }
}

// SAGAS

// helper methods
const getErrorCode = (preference: UpdatablePreference) =>
  preference === UpdatablePreference.GDPR_NOTICE ? GdprErrors.generic.savingNotice : GdprErrors.generic.savingConsent;

// update the default user gdpr preferences
function* updateUserPreferenceSaga(action: UpdatePreference) {
  try {
    const users: User[] = yield select(Ducks.Household.Selectors.data.getUsers);

    const defaultUser = users.find((user) => user.role === Role.DEFAULT) as User;

    yield put(
      Ducks.Household.Creators.updateUserPreference(
        { ...defaultUser, [action.meta.preference]: action.meta.preferenceValue },
        action.meta.preference
      )
    );

    const { resultSuccess } = yield race({
      resultSuccess: take(Ducks.Household.ActionType.updateUserPreference.SUCCESS),
      resultError: take(Ducks.Household.ActionType.updateUserPreference.FAIL),
    });

    if (resultSuccess) {
      yield put(Actions.updatePreferenceSuccess());
      if (action.meta.preference === UpdatablePreference.GDPR_CONSENT) {
        yield fork(
          addSettingsActionEvent,
          ActionsEventActionNames.ACCEPT_GDPR_CONSENT,
          action.meta.preferenceValue.accepted.toString(),
          undefined
        );
      }
    } else {
      throw XError.fromCode(getErrorCode(action.meta.preference));
    }
  } catch (e) {
    const error = XError.fromCode(getErrorCode(action.meta.preference));
    yield put(Actions.updatePreferenceFail(error));
    if (action.meta.preference === UpdatablePreference.GDPR_CONSENT) {
      yield fork(
        addSettingsActionEvent,
        ActionsEventActionNames.ACCEPT_GDPR_CONSENT,
        action.meta.preferenceValue.accepted.toString(),
        undefined,
        error
      );
    }
  }
}

export function* saga(): SagaIterator {
  yield takeLeading(ActionTypes.UPDATE_PREFERENCE, updateUserPreferenceSaga);
}

// SELECTORS

// Check if user has agreed to the current notice version
const userAgreedDocument = (userPref: GdprUserPreference | undefined, doc: GdprDocument | undefined): boolean => {
  return (
    doc !== undefined && // valid document available in faqs
    userPref !== undefined && // user has a preference for the document
    userPref.accepted && // the preference is accept
    (userPref.version as number) >= doc.version // the accepted version is correct regarding the current doc version
  );
};

const userAgreedCurrentNotice = (user: User | undefined, documents: GdprDocuments | undefined): boolean => {
  return userAgreedDocument(user?.gdprNotice, documents?.notice);
};

const userAgreedCurrentConsent = (user: User | undefined, documents: GdprDocuments | undefined): boolean => {
  return userAgreedDocument(user?.gdprConsent, documents?.consent);
};

const shouldShowConsent = (user: User | undefined, documents: GdprDocuments | undefined): boolean => {
  const doc = documents?.consent;
  const consent = user?.gdprConsent;
  if (doc === undefined) {
    return false; // no document is present, don't show nothing to the user (CI128-GDPR_UD_001.7)
  }
  if (consent === undefined) {
    return true; // doc is present and user has not yet responded to the privacy consent, show it to the user (CI128-GDPR_UD_001.6)
  }
  if (!consent.accepted) {
    return false; // doc is present and user has rejected the privacy consent, don't ask again (CI128-GDPR_UD_001.5)
  }
  if (consent.version !== undefined && consent.version >= doc.version) {
    return false; // doc is present and user has accepted the current or superior version of the privacy consent, don't ask again (CI128-GDPR_UD_001.4)
  }
  return true; // doc is present and user has accepted and previous version of the consent, ask again (CI128-GDPR_UD_001.6)
};

// check the documents (available in faqs) against the user preferences
// and returns the ones that are neccessary to present to the user
const getDocumentsToShow = (state: StoreState): GdprDocuments => {
  const users = Ducks.Household.Selectors.data.getUsers(state);
  const defaultUser = users.find((user) => user.role === Role.DEFAULT) as User;
  const gdprData = Ducks.Faqs.Selectors.getGdprData(state);
  // const isFtu = Ducks.Configs.Selectors.isFtu(state);

  // TODO: CHECK FTU LOGIC
  const showNotice = !userAgreedCurrentNotice(defaultUser, gdprData?.documents);
  // || (gdprData?.ftuRules?.notice?.forceUpdate && isFtu); // version is not lower but the rule states to show in FTU

  // TODO: CHECK FTU LOGIC
  const showConsent = shouldShowConsent(defaultUser, gdprData?.documents);
  // || (gdprData?.ftuRules?.consent?.forceUpdate && isFtu); // version is not lower but the rule states to show in FTU

  const showTermsAndConditions =
    defaultUser && // users already loaded
    gdprData?.documents.termsAndConditions; // valid document available in faqs

  return {
    notice: (showNotice && gdprData?.documents.notice) || undefined,
    consent: (showConsent && gdprData?.documents.consent) || undefined,
    termsAndConditions: (showTermsAndConditions && gdprData?.documents.termsAndConditions) || undefined,
  };
};

// Check if user has agreed to the current notice version
const hasAgreedToNotice = (state: StoreState): boolean => {
  const users = Ducks.Household.Selectors.data.getUsers(state);
  const defaultUser = users.find((user) => user.role === Role.DEFAULT) as User;
  const gdprData = Ducks.Faqs.Selectors.getGdprData(state);

  return userAgreedCurrentNotice(defaultUser, gdprData?.documents);
};

// Check if user has agreed to the current consent version
const hasAgreedToConsent = (state: StoreState): boolean => {
  const defaultUser = Ducks.Household.Selectors.data.getDefaultUser(state);
  const gdprData = Ducks.Faqs.Selectors.getGdprData(state);

  return userAgreedCurrentConsent(defaultUser, gdprData?.documents);
};

export const Selectors = {
  isProcessed: (state: StoreState): boolean => state.gdpr.processed === true,
  isProcessing: (state: StoreState): boolean => state.gdpr.processing === true,
  getError: (state: StoreState): XError | undefined => state.gdpr.error,
  getDocumentsToShow,
  getDocuments: (state: StoreState): GdprDocuments | undefined => Ducks.Faqs.Selectors.getGdprData(state)?.documents,
  hasAgreedToNotice,
  hasAgreedToConsent,
};
